import { useState } from 'react';
import { CheckCircle, ArrowRight, Info } from 'lucide-react';
import DemoRequestDialog from './DemoRequestDialog';
import { trackEvent } from '@/lib/analytics';
import SectionEyebrow from '@/components/SectionEyebrow';

const PLANS = [
  { id: 'monthly', title: 'Aylık', months: 1, base: 1200, integration: 0 },
  { id: 'yearly', title: '12 Aylık', months: 12, base: 10000, integration: 4200 },
  { id: 'six_months', title: '6 Aylık', months: 6, base: 6500, integration: 3300 },
];
const money = (amount) => new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(amount) + ' TL';
const FEATURES = [
  'QR menü oluşturma',
  'Sipariş yönetimi',
  'Reçete ve stok kontrolü',
  'Sadakat puanları ve kampanyalar',
  'Personel ve yetki yönetimi',
  'Detaylı satış raporları',
  'Ücretsiz sistem güncellemeleri',
  'Eğitim ve danışmanlık',
  'POS kurulum ve süreç desteği',
  '7/24 müşteri desteği',
];
const FAQS = [
  [
    'Demo talebi bir abonelik başlatır mı?',
    'Hayır. Sizi arayıp uygun bir tanıtım zamanı belirleriz. Demo ortamında ekranları birlikte inceler, sorularınızı yanıtlarız. Talep göndermek ödeme veya abonelik başlatmaz.',
  ],
  [
    'Kurulum ve eğitim için ayrıca ödeme yapar mıyım?',
    'Tilki kurulumu için ek ücret talep etmiyoruz. Eğitim ve danışmanlık ana pakete dahildir. Hugin veya Ödeal cihazları, varsa sağlayıcı ücretleri ve sözleşmeleri ilgili sağlayıcıyla yürütülür.',
  ],
  [
    'Mevcut cihazım ve POS sistemim uygun mu?',
    'Kullandığınız cihazı ve varsa POS modelini tanıtım görüşmesinde paylaşabilirsiniz. İşletmenizin ihtiyaçlarına göre uyumluluğu ve kurulum kapsamını birlikte değerlendirelim.',
  ],
  [
    'Destek için size nasıl ulaşabilirim?',
    'Mevcut müşterilerimize 7/24 destek sunuyoruz. Demo ve abonelik başvurularına dönüşler ile abonelik başvurularının işleme alınması Pazartesi–Cuma 09:00–18:00 saatleri arasında yapılır. Telefon veya iletisim@tilkiotomasyon.com üzerinden bize ulaşabilirsiniz.',
  ],
  [
    'Yemek platformu entegrasyonları hangi paketlerde var?',
    'Yemeksepeti, Trendyol Yemek ve Migros Yemek entegrasyonları 6 aylık ve 12 aylık paketlere eklenebilir. Aylık pakette sunulmaz.',
  ],
  [
    'Entegrasyon ücreti toplamı nasıl değiştirir?',
    '6 aylık ana paket 6.500 TL, entegrasyon paketi 3.300 TL; birlikte 9.800 TL + KDV’dir. 12 aylık ana paket 10.000 TL, entegrasyon paketi 4.200 TL; birlikte 14.200 TL + KDV’dir. Entegrasyon tutarı seçilen dönemin tamamı içindir.',
  ],
  ['Fiyatlara KDV dahil mi?', 'Gösterilen ana paket, entegrasyon ve toplam tutarlar KDV hariçtir. KDV ayrıca eklenir.'],
  ['Paket süremi değiştirebilir miyim?', 'Farklı bir abonelik süresine geçiş için bizimle iletişime geçebilirsiniz.'],
];

export default function Pricing() {
  const [integrations, setIntegrations] = useState({ six_months: false, yearly: false });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [message, setMessage] = useState('');

  const selectPlan = (plan, added, total) => {
    trackEvent('lead-cta-click', { placement: 'pricing' });
    trackEvent('pricing-plan-click', { plan: plan.title, integration: added, total });
    setMessage(
      `Tilki Otomasyon ${plan.title} paket hakkında bilgi almak istiyorum. Ana paket: ${money(plan.base)}. Yemek platformu entegrasyonları: ${added ? money(plan.integration) : 'eklenmedi'}. Toplam: ${money(total)} + KDV.`
    );
    setIsDialogOpen(true);
  };

  return (
    <section id="pricing" className="relative scroll-mt-20">
      <div className="section-y-lg bg-surface-2">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <SectionEyebrow className="mb-4">Şeffaf fiyatlandırma</SectionEyebrow>
            <h2 className="type-h2 mb-4">İşletmenize uygun süreyi seçin</h2>
            <p className="type-body-lg">
              Ana paketinizi seçin, ihtiyacınız varsa yemek platformu entegrasyonlarını ekleyin. Toplam tutarı hemen
              görün.
            </p>
            <p className="mt-3 text-sm font-semibold text-brand-700">Kurulum ücretsiz · Tüm fiyatlar + KDV</p>
          </div>

          <div className="grid items-stretch gap-5 lg:grid-cols-3">
            {PLANS.map((plan) => {
              const added = plan.months > 1 && integrations[plan.id];
              const extra = added ? plan.integration : 0;
              const total = plan.base + extra;
              return (
                <article
                  key={plan.id}
                  className={`relative flex min-w-0 flex-col rounded-2xl border bg-surface p-6 ${plan.id === 'yearly' ? 'border-brand-600 ring-2 ring-brand-600/20 shadow-card-lift' : 'border-hairline shadow-card'}`}
                >
                  {plan.id === 'yearly' && (
                    <span className="absolute -top-3 right-5 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                      Önerilen paket
                    </span>
                  )}
                  <h3 className="font-poppins text-2xl font-bold text-ink">{plan.title}</h3>
                  <p className="mt-2 text-sm text-ink-muted">Tilki Otomasyon ana paketi</p>
                  <p className="mt-5 font-poppins text-4xl font-bold tracking-tight text-ink-strong">
                    {money(plan.base)}
                  </p>
                  <p className="mt-2 text-sm text-ink-muted">
                    + KDV / {plan.months === 1 ? 'ay' : plan.months + ' aylık dönem'}
                  </p>

                  <div className="my-6 flex-1 rounded-xl border border-hairline bg-surface-2 p-4">
                    <p className="text-sm font-semibold text-ink">Yemek platformu entegrasyonları</p>
                    <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                      Yemeksepeti · Trendyol Yemek · Migros Yemek
                    </p>
                    {plan.months > 1 ? (
                      <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-brand-200 bg-surface p-3">
                        <input
                          type="checkbox"
                          checked={!!added}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setIntegrations((prev) => ({ ...prev, [plan.id]: checked }));
                            trackEvent('pricing-integration-toggle', { plan: plan.title, selected: checked });
                          }}
                          className="mt-1 h-5 w-5 shrink-0 accent-brand-600"
                          aria-label={plan.title + ' yemek platformu entegrasyonlarını ekle'}
                        />
                        <span className="text-sm">
                          <span className="block font-semibold text-ink">Entegrasyonları ekle</span>
                          <span className="mt-1 block text-brand-700">+ {money(plan.integration)} + KDV</span>
                          <span className="mt-1 block text-xs text-ink-muted">
                            {plan.months} aylık dönemin tamamı için
                          </span>
                        </span>
                      </label>
                    ) : (
                      <p className="mt-4 rounded-lg border border-hairline bg-surface p-3 text-sm text-ink-muted">
                        Aylık pakette sunulmaz. Entegrasyonlar için 6 veya 12 aylık paketi seçin.
                      </p>
                    )}
                  </div>

                  <div aria-live="polite" aria-atomic="true" className="border-t border-hairline pt-4">
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between gap-2">
                        <dt className="text-ink-muted">Ana paket</dt>
                        <dd>{money(plan.base)}</dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-ink-muted">Entegrasyon farkı</dt>
                        <dd className={added ? 'font-semibold text-brand-700' : 'text-ink-muted'}>
                          {added ? '+ ' + money(extra) : 'Eklenmedi'}
                        </dd>
                      </div>
                      <div className="flex flex-wrap items-baseline justify-between gap-2 pt-3">
                        <dt className="font-semibold text-ink">Dönem toplamı</dt>
                        <dd className="font-poppins text-2xl font-bold text-brand-700">{money(total)}</dd>
                      </div>
                    </dl>
                    <p className="mt-1 text-right text-xs text-ink-muted">+ KDV · {plan.months} aylık toplam</p>{' '}
                    <div className="mt-4 rounded-lg bg-surface-2 p-3">
                      <p className="text-sm font-semibold text-ink">
                        Aylık karşılığı: {plan.months > 1 ? 'yaklaşık ' : ''}
                        {money(total / plan.months)} + KDV
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                        {plan.months > 1
                          ? 'Seçtiğiniz dönem toplamının aylık karşılığıdır; aylık ödeme veya taksit tutarı değildir.'
                          : '1 aylık paket tutarıdır.'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modern-btn-primary mt-6 w-full"
                    onClick={() => selectPlan(plan, !!added, total)}
                    aria-label={plan.title + ' paket için bilgi al'}
                  >
                    Paket hakkında bilgi al <ArrowRight className="ml-2 h-4 w-4 shrink-0" aria-hidden="true" />
                  </button>
                </article>
              );
            })}
          </div>

          <div className="mt-8 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
            <h3 className="font-poppins text-xl font-semibold text-ink">Her ana pakete dahil</h3>
            <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-ink-muted">
                  <CheckCircle className="h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div
            className="mt-8 rounded-2xl border border-hairline bg-surface p-6 sm:p-8"
            aria-labelledby="pos-solutions-title"
          >
            <SectionEyebrow className="mb-3">POS entegrasyonları</SectionEyebrow>
            <h3 id="pos-solutions-title" className="font-poppins text-xl font-semibold text-ink">
              Uyumlu POS Çözümleri
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-muted">
              Hugin ve Ödeal ile POS kurulum sürecinde yanınızdayız.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                { name: 'Hugin', logo: 'hugin', width: 1100, height: 339 },
                { name: 'Ödeal', logo: 'odeal', width: 768, height: 252 },
              ].map((provider) => (
                <div
                  key={provider.name}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-hairline bg-white p-5"
                >
                  <img
                    src={`/img/partners/${provider.logo}.png`}
                    alt={provider.name}
                    width={provider.width}
                    height={provider.height}
                    loading="lazy"
                    decoding="async"
                    className={`h-14 shrink-0 object-contain ${provider.logo === 'hugin' ? 'w-40' : 'w-32'}`}
                  />
                  <span className="inline-flex items-center gap-2 text-xs font-medium text-brand-700">
                    <CheckCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    POS kurulum desteği
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-6 flex items-start gap-2 border-t border-hairline pt-5 text-sm leading-relaxed text-ink-muted">
              <Info className="h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />
              <span>
                Hugin ve Ödeal POS entegrasyonu kurulumunda Tilki ek ücret talep etmez; süreç boyunca destek sağlar. POS
                cihazı, varsa ücretler ve sözleşme koşulları doğrudan Hugin veya Ödeal ile yürütülür.
              </span>
            </p>
          </div>
        </div>
      </div>
      <div className="section-y bg-surface">
        <div className="container mx-auto max-w-3xl px-4">
          <h3 className="type-h3 mb-8 text-center">Sıkça sorulan sorular</h3>
          <div className="space-y-3">
            {FAQS.map(([question, answer]) => (
              <details key={question} className="rounded-xl border border-hairline p-5">
                <summary className="cursor-pointer font-semibold text-ink">{question}</summary>
                <p className="mt-3 leading-relaxed text-ink-muted">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
      <DemoRequestDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} initialMessage={message} />
    </section>
  );
}
